import React from "react";
import BaseDataTable from "react-data-table-component";
import styled from "styled-components";
import { tokens } from "../../theme/tokens";
import Spinner from "./Spinner";
import EmptyState from "./EmptyState";

// DataTable — thin wrapper over react-data-table-component that injects the
// shared branded `customStyles`, so every table across the portal looks the
// same (rounded container, branded header row, comfortable rows, hover, zebra
// optional). Drop-in: pass the same `columns` / `data` you already use.
//
//   <DataTable columns={cols} data={rows} zebra />
//
// Per-page `Styles.customTableStyles` should be replaced by this wrapper.

const { colors, radii, shadows, spacing, typography } = tokens;

export const tableCustomStyles = {
    table: {
        style: {
            backgroundColor: colors.surface,
        },
    },
    responsiveWrapper: {
        style: {
            borderRadius: radii.lg,
            border: `1px solid ${colors.border}`,
            boxShadow: shadows.sm,
            overflow: "hidden",
        },
    },
    head: {
        style: {
            fontFamily: typography.fonts.body,
        },
    },
    headRow: {
        style: {
            minHeight: "48px",
            backgroundColor: colors.cream,
            borderBottomWidth: "1px",
            borderBottomColor: colors.border,
        },
    },
    headCells: {
        style: {
            fontSize: typography.size.xs,
            fontWeight: typography.weight.bold,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: colors.muted,
            paddingLeft: "16px",
            paddingRight: "16px",
        },
    },
    rows: {
        style: {
            minHeight: "56px",
            fontFamily: typography.fonts.body,
            fontSize: typography.size.base,
            color: colors.body,
            borderBottomColor: colors.divider + " !important",
        },
        highlightOnHoverStyle: {
            backgroundColor: colors.rose,
            transitionDuration: "0.15s",
            transitionProperty: "background-color",
            outline: "none",
        },
        stripedStyle: {
            backgroundColor: colors.surfaceMuted,
        },
    },
    cells: {
        style: {
            paddingLeft: "16px",
            paddingRight: "16px",
        },
    },
    pagination: {
        style: {
            fontFamily: typography.fonts.body,
            fontSize: typography.size.sm,
            color: colors.muted,
            borderTopColor: colors.divider,
            backgroundColor: colors.surface,
        },
        pageButtonsStyle: {
            borderRadius: radii.sm,
            height: "32px",
            width: "32px",
            padding: "4px",
            margin: "0 2px",
            fill: colors.muted,
            "&:hover:not(:disabled)": { backgroundColor: colors.surfaceSunken },
            "&:disabled": { fill: colors.faint },
        },
    },
};

// Wrap the table so styled-components can style pieces react-data-table exposes
// only via class names (e.g. the sort arrow, selected rows).
const Wrap = styled.div`
    .rdt_TableCol_Sortable:hover { color: ${colors.primary} !important; opacity: 1; }
    .rdt_TableRow:last-child { border-bottom: 0 !important; }
`;

const defaultProgress = <Spinner center size={28} label="Loading…" />;
const defaultEmpty = <EmptyState title="Nothing here yet" description="No records to display." />;

export default function DataTable({
    zebra = false,
    highlightOnHover = true,
    pointerOnHover = false,
    customStyles,
    progressComponent,
    noDataComponent,
    ...rest
}) {
    return (
        <Wrap>
            <BaseDataTable
                striped={zebra}
                highlightOnHover={highlightOnHover}
                pointerOnHover={pointerOnHover}
                persistTableHead
                customStyles={{ ...tableCustomStyles, ...(customStyles || {}) }}
                progressComponent={progressComponent || defaultProgress}
                noDataComponent={noDataComponent || defaultEmpty}
                {...rest}
            />
        </Wrap>
    );
}
